import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { 
  School, Users, FileText, Loader2, ArrowLeft, Copy, Check, Trash2,
  Trophy, Share2, Clock, ChevronRight, Medal, BarChart3, CheckCircle, XCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const ClassroomDetail = () => {
  const { classroomId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [classroom, setClassroom] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myResults, setMyResults] = useState(null);
  const [tests, setTests] = useState([]);
  const [lectureNotes, setLectureNotes] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [showShareNotes, setShowShareNotes] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    fetchClassroom();
    fetchLeaderboard();
  }, [classroomId]);

  const fetchClassroom = async () => {
    try {
      const response = await axios.get(`${API}/classrooms/${classroomId}`, {
        headers: getAuthHeaders(token)
      });
      setClassroom(response.data);
      
      // Fetch teacher's tests and lecture notes if teacher
      if (response.data.is_teacher) {
        const [testsRes, notesRes] = await Promise.all([
          axios.get(`${API}/tests`, { headers: getAuthHeaders(token) }),
          axios.get(`${API}/lecture-notes`, { headers: getAuthHeaders(token) })
        ]);
        setTests(testsRes.data);
        setLectureNotes(notesRes.data);
      } else {
        // Fetch student's own results
        try {
          const resultsRes = await axios.get(`${API}/classrooms/${classroomId}/my-results`, {
            headers: getAuthHeaders(token)
          });
          setMyResults(resultsRes.data);
        } catch (e) {
          console.error("Failed to fetch results:", e);
        }
      }
    } catch (error) {
      toast.error("Failed to load classroom");
      navigate("/classrooms");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/classrooms/${classroomId}/leaderboard`, {
        headers: getAuthHeaders(token)
      });
      setLeaderboard(response.data);
    } catch (error) {
      // Ignore
    }
  };

  const handleShareTest = async () => {
    if (!selectedTest) return;
    setSharing(true);
    try {
      await axios.post(`${API}/classrooms/${classroomId}/share-test`, 
        { test_id: selectedTest },
        { headers: getAuthHeaders(token) }
      );
      toast.success("Test shared with classroom!");
      setShowShare(false);
      setSelectedTest(null);
      fetchClassroom();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to share test");
    } finally {
      setSharing(false);
    }
  };

  const handleShareNotes = async () => {
    if (!selectedNote) return;
    setSharing(true);
    try {
      await axios.post(`${API}/classrooms/${classroomId}/share-notes`, 
        { notes_id: selectedNote },
        { headers: getAuthHeaders(token) }
      );
      toast.success("Lecture notes shared with classroom!");
      setShowShareNotes(false);
      setSelectedNote(null);
      fetchClassroom();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to share lecture notes");
    } finally {
      setSharing(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/classrooms/${classroomId}`, {
        headers: getAuthHeaders(token)
      });
      toast.success("Classroom deleted");
      navigate("/classrooms");
    } catch (error) {
      toast.error("Failed to delete classroom");
    }
  };

  const copyJoinCode = () => {
    navigator.clipboard.writeText(classroom.join_code);
    setCopiedCode(true);
    toast.success("Join code copied!");
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const copyInviteLink = () => {
    const inviteLink = `${window.location.origin}/join/${classroom.join_code}`;
    navigator.clipboard.writeText(inviteLink);
    toast.success("Invite link copied! Share this with your students.");
  };

  const getMedalColor = (index) => {
    switch (index) {
      case 0: return "text-yellow-500";
      case 1: return "text-slate-400";
      case 2: return "text-orange-400";
      default: return "text-slate-300";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  if (!classroom) return null;

  const isTeacher = classroom.is_teacher;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="classroom-detail">
      <DashboardHeader />

      <main className="container-main py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate("/classrooms")} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#4F46E5] to-[#14B8A6] rounded-xl flex items-center justify-center">
                <School className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {classroom.name}
                </h1>
                {classroom.description && (
                  <p className="text-slate-500">{classroom.description}</p>
                )}
              </div>
            </div>
          </div>
          {isTeacher && (
            <div className="flex items-center gap-3 flex-wrap">
              <Button variant="outline" onClick={copyJoinCode} className="gap-2">
                {copiedCode ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {classroom.join_code}
              </Button>
              <Button variant="outline" onClick={copyInviteLink} className="gap-2 text-[#4F46E5] border-[#4F46E5]/30 hover:bg-[#4F46E5]/10">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Invite Link
              </Button>
              <Button variant="outline" onClick={() => setShowShare(true)} className="gap-2">
                <Share2 className="w-4 h-4" />
                Share Test
              </Button>
              <Button variant="ghost" onClick={() => setShowDelete(true)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-[#4F46E5] mx-auto mb-2" />
              <p className="text-2xl font-bold">{classroom.students?.length || 0}</p>
              <p className="text-xs text-slate-500">Students</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 text-orange-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">{classroom.shared_tests?.length || 0}</p>
              <p className="text-xs text-slate-500">Shared Tests</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold">
                {leaderboard.length > 0 ? `${leaderboard[0]?.average_score || 0}%` : '-'}
              </p>
              <p className="text-xs text-slate-500">Top Score</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-[#14B8A6] mx-auto mb-2" />
              <p className="text-2xl font-bold">
                {new Date(classroom.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
              <p className="text-xs text-slate-500">Created</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="tests" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-sm">
            <TabsTrigger value="tests" className="gap-2 data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <FileText className="w-4 h-4" />
              Shared Tests
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-2 data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <FileText className="w-4 h-4" />
              Lecture Notes
            </TabsTrigger>
            {!isTeacher && (
              <TabsTrigger value="my-results" className="gap-2 data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
                <BarChart3 className="w-4 h-4" />
                My Results
              </TabsTrigger>
            )}
            <TabsTrigger value="leaderboard" className="gap-2 data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
              <Trophy className="w-4 h-4" />
              Leaderboard
            </TabsTrigger>
            {isTeacher && (
              <TabsTrigger value="students" className="gap-2 data-[state=active]:bg-[#4F46E5] data-[state=active]:text-white">
                <Users className="w-4 h-4" />
                Students
              </TabsTrigger>
            )}
          </TabsList>

          {/* Shared Tests Tab */}
          <TabsContent value="tests">
            {classroom.shared_tests?.length === 0 ? (
              <Card className="card-base">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No tests shared yet</p>
                  {isTeacher && (
                    <Button onClick={() => setShowShare(true)} className="mt-4 btn-primary gap-2">
                      <Share2 className="w-4 h-4" />
                      Share a Test
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {isTeacher && (
                  <div className="flex justify-end">
                    <Button onClick={() => setShowShare(true)} className="btn-primary gap-2">
                      <Share2 className="w-4 h-4" />
                      Share Another Test
                    </Button>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {classroom.shared_tests.map((test) => (
                    <Card key={test.test_id} className="card-base hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{test.title || 'Test'}</h4>
                            <p className="text-sm text-slate-500">
                              {test.questions_count || 0} questions
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Shared {new Date(test.shared_at).toLocaleDateString()}
                            </p>
                          </div>
                          {!isTeacher && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/tests/${test.test_id}/take?classroom=${classroomId}`)}
                              className="gap-1"
                            >
                              Take Test
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Shared Lecture Notes Tab */}
          <TabsContent value="notes">
            {classroom.shared_notes?.length === 0 ? (
              <Card className="card-base">
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No lecture notes shared yet</p>
                  {isTeacher && (
                    <Button onClick={() => setShowShareNotes(true)} className="mt-4 btn-primary gap-2">
                      <Share2 className="w-4 h-4" />
                      Share Lecture Notes
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {isTeacher && (
                  <div className="flex justify-end">
                    <Button onClick={() => setShowShareNotes(true)} className="btn-primary gap-2">
                      <Share2 className="w-4 h-4" />
                      Share More Notes
                    </Button>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {classroom.shared_notes.map((note) => (
                    <Card key={note.notes_id} className="card-base hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium">{note.title || 'Lecture Notes'}</h4>
                            <p className="text-sm text-slate-500 line-clamp-2">
                              {note.summary || 'No summary'}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Shared {new Date(note.shared_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/lecture-notes/${note.notes_id}`)}
                            className="gap-1 ml-2"
                          >
                            View
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* My Results Tab (Students only) */}
          {!isTeacher && (
            <TabsContent value="my-results">
              <div className="space-y-6">
                {/* Summary Card */}
                <Card className="card-base bg-gradient-to-br from-indigo-50 to-purple-50">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                          <BarChart3 className="w-8 h-8 text-[#4F46E5]" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">Your Progress</h3>
                          <p className="text-slate-600">
                            {myResults?.completed_tests || 0} of {myResults?.total_tests || 0} tests completed
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-3xl font-bold text-[#4F46E5]">
                            {myResults?.average_score || 0}%
                          </p>
                          <p className="text-sm text-slate-500">Average Score</p>
                        </div>
                        <div className="h-12 w-px bg-slate-200" />
                        <div className="text-center">
                          <p className="text-3xl font-bold text-green-600">
                            {myResults?.total_tests ? Math.round((myResults.completed_tests / myResults.total_tests) * 100) : 0}%
                          </p>
                          <p className="text-sm text-slate-500">Completion</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Test Results Table */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle>Test Results</CardTitle>
                    <CardDescription>Your performance on shared tests</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!myResults?.results?.length ? (
                      <div className="text-center py-8 text-slate-400">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No tests available yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {myResults.results.map((result) => (
                          <div
                            key={result.test_id}
                            className={`flex items-center justify-between p-4 rounded-xl border ${
                              result.completed 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {result.completed ? (
                                <CheckCircle className="w-6 h-6 text-green-500" />
                              ) : (
                                <div className="w-6 h-6 rounded-full border-2 border-slate-300" />
                              )}
                              <div>
                                <h4 className="font-medium text-slate-900">{result.title}</h4>
                                <p className="text-sm text-slate-500">
                                  {result.completed 
                                    ? `Completed ${new Date(result.submitted_at).toLocaleDateString()}`
                                    : 'Not started yet'
                                  }
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {result.completed ? (
                                <div className="text-right">
                                  <p className={`text-xl font-bold ${
                                    result.percentage >= 80 ? 'text-green-600' :
                                    result.percentage >= 60 ? 'text-yellow-600' : 'text-red-600'
                                  }`}>
                                    {result.percentage}%
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {result.score}/{result.total} correct
                                  </p>
                                </div>
                              ) : (
                                <Button
                                  onClick={() => navigate(`/tests/${result.test_id}/take?classroom=${classroomId}`)}
                                  className="btn-primary gap-2"
                                >
                                  Take Test
                                  <ChevronRight className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Class Leaderboard
                </CardTitle>
                <CardDescription>Rankings based on average test scores</CardDescription>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No scores yet. Complete some tests to see the leaderboard!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {leaderboard.map((student, idx) => (
                      <div 
                        key={student.user_id}
                        className={`flex items-center gap-4 p-4 rounded-xl ${
                          idx === 0 ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200' :
                          idx === 1 ? 'bg-slate-50' :
                          idx === 2 ? 'bg-orange-50/50' : 'bg-white border'
                        }`}
                      >
                        <div className="flex items-center justify-center w-10 h-10">
                          {idx < 3 ? (
                            <Medal className={`w-8 h-8 ${getMedalColor(idx)}`} />
                          ) : (
                            <span className="text-lg font-bold text-slate-400">#{idx + 1}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{student.name}</p>
                          <p className="text-xs text-slate-500">{student.tests_taken} tests completed</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${
                            idx === 0 ? 'text-yellow-600' : 'text-slate-700'
                          }`}>
                            {student.average_score}%
                          </p>
                          <p className="text-xs text-slate-400">avg score</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Students Tab (Teacher only) */}
          {isTeacher && (
            <TabsContent value="students">
              <div className="space-y-6">
                {/* Invite Section Card */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle>Invite Students</CardTitle>
                    <CardDescription>Share the code or link with your students</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1 bg-white rounded-lg p-3 border">
                          <p className="text-xs text-slate-500 mb-1">Join Code</p>
                          <p className="font-mono text-lg font-bold text-[#4F46E5]">{classroom.join_code}</p>
                        </div>
                        <div className="flex-1 bg-white rounded-lg p-3 border">
                          <p className="text-xs text-slate-500 mb-1">Invite Link</p>
                          <p className="text-sm text-slate-700 truncate">{window.location.origin}/join/{classroom.join_code}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button variant="outline" size="sm" onClick={copyJoinCode} className="gap-1">
                          <Copy className="w-3 h-3" /> Copy Code
                        </Button>
                        <Button variant="outline" size="sm" onClick={copyInviteLink} className="gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Student Progress Card */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#4F46E5]" />
                      Student Progress
                    </CardTitle>
                    <CardDescription>
                      Track your students' test completion and scores
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {classroom.students?.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No students enrolled yet</p>
                        <p className="text-sm mt-1">Share your join code or invite link with students</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-slate-50">
                              <th className="text-left p-3 text-sm font-medium text-slate-500">Student</th>
                              <th className="text-left p-3 text-sm font-medium text-slate-500">Email</th>
                              <th className="text-center p-3 text-sm font-medium text-slate-500">Tests Completed</th>
                              <th className="text-center p-3 text-sm font-medium text-slate-500">Avg Score</th>
                              <th className="text-left p-3 text-sm font-medium text-slate-500">Progress</th>
                              <th className="text-left p-3 text-sm font-medium text-slate-500">Joined</th>
                            </tr>
                          </thead>
                          <tbody>
                            {classroom.students.map((student) => {
                              const studentLeaderboard = leaderboard.find(l => l.user_id === student.user_id);
                              const testsCompleted = studentLeaderboard?.tests_taken || 0;
                              const totalTests = classroom.shared_tests?.length || 0;
                              const avgScore = studentLeaderboard?.average_score || 0;
                              const completionRate = totalTests > 0 ? Math.round((testsCompleted / totalTests) * 100) : 0;
                              
                              return (
                                <tr key={student.user_id} className="border-b hover:bg-slate-50">
                                  <td className="p-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center">
                                        <span className="text-sm font-medium text-[#4F46E5]">
                                          {student.name?.charAt(0)?.toUpperCase() || '?'}
                                        </span>
                                      </div>
                                      <span className="font-medium">{student.name}</span>
                                    </div>
                                  </td>
                                  <td className="p-3 text-slate-500 text-sm">{student.email}</td>
                                  <td className="p-3 text-center">
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
                                      {testsCompleted}/{totalTests}
                                    </span>
                                  </td>
                                  <td className="p-3 text-center">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${
                                      avgScore >= 80 ? 'bg-green-50 text-green-700' :
                                      avgScore >= 60 ? 'bg-yellow-50 text-yellow-700' :
                                      avgScore > 0 ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-500'
                                    }`}>
                                      {avgScore > 0 ? `${avgScore}%` : '—'}
                                    </span>
                                  </td>
                                  <td className="p-3">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[100px]">
                                        <div 
                                          className={`h-full rounded-full ${
                                            completionRate === 100 ? 'bg-green-500' :
                                            completionRate >= 50 ? 'bg-yellow-500' : 'bg-indigo-500'
                                          }`}
                                          style={{ width: `${completionRate}%` }}
                                        />
                                      </div>
                                      <span className="text-xs text-slate-500">{completionRate}%</span>
                                    </div>
                                  </td>
                                  <td className="p-3 text-slate-500 text-sm">
                                    {new Date(student.joined_at).toLocaleDateString()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Share Test Dialog */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Test with Classroom</DialogTitle>
            <DialogDescription>
              Select a test to share with your students
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4 max-h-96 overflow-y-auto">
            {tests.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tests available to share</p>
                <Button onClick={() => navigate("/books")} className="mt-4" variant="outline">
                  Create a Test First
                </Button>
              </div>
            ) : (
              tests.map((test) => (
                <div
                  key={test.test_id}
                  className={`p-4 border rounded-xl cursor-pointer transition-all ${
                    selectedTest === test.test_id 
                      ? 'border-[#4F46E5] bg-indigo-50' 
                      : 'hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedTest(test.test_id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{test.title}</h4>
                      <p className="text-sm text-slate-500">
                        {test.questions?.length || 0} questions
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 ${
                      selectedTest === test.test_id 
                        ? 'border-[#4F46E5] bg-[#4F46E5]' 
                        : 'border-slate-300'
                    }`}>
                      {selectedTest === test.test_id && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {tests.length > 0 && (
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowShare(false)}>Cancel</Button>
              <Button 
                onClick={handleShareTest} 
                disabled={!selectedTest || sharing} 
                className="btn-primary gap-2"
              >
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                Share Test
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Lecture Notes Dialog */}
      <Dialog open={showShareNotes} onOpenChange={setShowShareNotes}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Lecture Notes with Classroom</DialogTitle>
            <DialogDescription>
              Select lecture notes to share with your students
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4 max-h-96 overflow-y-auto">
            {lectureNotes.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No lecture notes available to share</p>
                <Button onClick={() => navigate("/lecture-notes")} className="mt-4" variant="outline">
                  Create Lecture Notes First
                </Button>
              </div>
            ) : (
              lectureNotes.map((note) => (
                <div
                  key={note.notes_id}
                  className={`p-4 border rounded-xl cursor-pointer transition-all ${
                    selectedNote === note.notes_id 
                      ? 'border-[#4F46E5] bg-indigo-50' 
                      : 'hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedNote(note.notes_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium">{note.title}</h4>
                      <p className="text-sm text-slate-500 line-clamp-1">
                        {note.summary || 'No summary'}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 ml-3 flex-shrink-0 ${
                      selectedNote === note.notes_id 
                        ? 'border-[#4F46E5] bg-[#4F46E5]' 
                        : 'border-slate-300'
                    }`}>
                      {selectedNote === note.notes_id && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {lectureNotes.length > 0 && (
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowShareNotes(false)}>Cancel</Button>
              <Button 
                onClick={handleShareNotes} 
                disabled={!selectedNote || sharing} 
                className="btn-primary gap-2"
              >
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                Share Notes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Classroom?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{classroom.name}" and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClassroomDetail;
